// title-art.ts — render the shell's title-screen backdrop (titlescreenbkgd.png)
// out of the texture atlas so the HTML menu shows the REAL artwork (HTML/DOM UI
// shell plan, Phase 2 faithful-art pass). It's the same bitmap the canvas
// ShellForm paints (createTBitmap('titlescreenbkgd.png')), rendered through the
// same CreateTBitmap + BltTo path — no new asset pipeline, pack-sourced.
//
// extractTitleArt is node-testable (no DOM); titleArtToDataUrl is the browser
// step (Canvas → PNG data URL for a CSS background-image).

import { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import { ksideNeutral } from '../gfx/tbitmap.ts';
import type { TexAtlasMgr } from '../gfx/texatlasmgr.ts';
import { ClientAnimationData } from '../gfx/animdata.ts';
import type { PackFileReader } from '../io/packfile.ts';

export interface TitleArt {
  cx: number;
  cy: number;
  rgba: Uint8Array;
}

/** Render a named atlas bitmap (e.g. the title backdrop 'titlescreenbkgd.png'
 *  or the wordmark 'title.png') into a plain RGBA buffer; null if the pack has
 *  no such bitmap. Uses the original (logical) size so the clipped atlas content
 *  lands at its proper offset. */
export function extractAtlasArt(tam: TexAtlasMgr, name: string): TitleArt | null {
  const tbm = tam.CreateTBitmap(name);
  if (tbm === null) return null;
  const { cx, cy } = tbm.GetSize();
  if (cx <= 0 || cy <= 0) return null;
  const dib = new CpuDibBitmap(cx, cy);
  tbm.BltTo(dib, 0, 0, ksideNeutral, null);
  return { cx, cy, rgba: dib.rgba };
}

/** The 9-slice button bitmaps (misccontrols.ts ButtonControl: left cap + tiled
 *  middle + right cap, up/down states) — extracted as RGBA for the DOM buttons. */
export interface ButtonArt {
  leftUp: TitleArt;
  midUp: TitleArt;
  rightUp: TitleArt;
  leftDown: TitleArt;
  midDown: TitleArt;
  rightDown: TitleArt;
}

/** Extract the shared button 9-slice art (null if the pack lacks any piece — the
 *  DOM then falls back to CSS buttons). */
export function extractButtonArt(tam: TexAtlasMgr): ButtonArt | null {
  const names = [
    'buttonleftup',
    'buttonmidup',
    'buttonrightup',
    'buttonleftdown',
    'buttonmiddown',
    'buttonrightdown',
  ] as const;
  const a = names.map((n) => extractAtlasArt(tam, `${n}.png`));
  if (a.some((x) => x === null)) return null;
  const [leftUp, midUp, rightUp, leftDown, midDown, rightDown] = a as TitleArt[];
  return { leftUp, midUp, rightUp, leftDown, midDown, rightDown };
}

/** Browser: encode the title art as a PNG data URL. The CpuDibBitmap rgba is in
 *  ImageData (RGBA) byte order — the same buffer the CPU present path blits. */
export function titleArtToDataUrl(art: TitleArt): string {
  const canvas = document.createElement('canvas');
  canvas.width = art.cx;
  canvas.height = art.cy;
  const ctx = canvas.getContext('2d')!;
  const buf = new Uint8ClampedArray(art.rgba.length);
  buf.set(art.rgba);
  ctx.putImageData(new ImageData(buf, art.cx, art.cy), 0, 0);
  return canvas.toDataURL('image/png');
}

// Paint a TitleArt at (x, 0). `clipW`, if given, limits the painted region to the
// art's leftmost clipW px (so a tiled middle slice can't overrun the right cap).
function putArt(ctx: CanvasRenderingContext2D, art: TitleArt, x: number, clipW?: number): void {
  const buf = new Uint8ClampedArray(art.rgba.length);
  buf.set(art.rgba);
  const img = new ImageData(buf, art.cx, art.cy);
  if (clipW === undefined) ctx.putImageData(img, x, 0);
  else ctx.putImageData(img, x, 0, 0, 0, clipW, art.cy);
}

/** Browser: composite a 9-slice button into ONE bitmap at the art's native
 *  height, `wArt` px wide — exactly what the canvas ButtonControl.OnPaint draws
 *  (misccontrols.ts:387-393): BltTo the left cap, FillTo (tile) the middle, BltTo
 *  the right cap. The full button at the art's natural aspect (≈ left+mid+right);
 *  scaling this SINGLE image uniformly avoids the seams and line-doubling that
 *  per-layer CSS background tiling produced. `down` selects the pressed art. */
export function composeButtonDataUrl(art: ButtonArt, wArt: number, down: boolean): string {
  const left = down ? art.leftDown : art.leftUp;
  const mid = down ? art.midDown : art.midUp;
  const right = down ? art.rightDown : art.rightUp;
  const h = mid.cy;
  const w = Math.max(left.cx + right.cx, Math.round(wArt));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  putArt(ctx, left, 0);
  const midR = w - right.cx;
  for (let x = left.cx; x < midR; x += mid.cx) {
    const fits = x + mid.cx <= midR;
    putArt(ctx, mid, x, fits ? undefined : midR - x); // clip the final tile off the right cap
  }
  putArt(ctx, right, midR);
  return canvas.toDataURL('image/png');
}

/** The kside1 (player-1 team colour) the canvas HelpControl draws the manual frames with
 *  (ui/help.ts:1003 `DrawFrame(index, 0, ..., 1 (kside1))`). */
const KSIDE_HELP = 1;

/**
 * Resolve a help <IMG SRC="file.anir"> to a PNG data URL — the SAME path the canvas
 * HelpControl renders inline (ui/help.ts's <IMG> arm + PaintChunk's knChunkAniData):
 * LoadAnimationData(name) → GetStripIndex('help') → GetBounds(idx, 0) → DrawFrame(idx, 0)
 * onto a CpuDibBitmap, then encode that bitmap (the same CpuDibBitmap→PNG path titleArt-
 * ToDataUrl uses). Returns null (so the caller renders alt text + warns) when the file isn't
 * in the pack, has no 'help' strip, or yields an empty frame — never throws.
 *
 * Reuses ClientAnimationData unchanged (the byte-exact .anir reader the game renderer uses);
 * the .anir lookup is case-insensitive (PackFileReader.MapFile), so "HQ.anir" resolves to the
 * pack's "hq.anir" exactly as the C++ Help does.
 */
export function resolveHelpImageDataUrl(
  pakr: PackFileReader,
  tam: TexAtlasMgr,
  src: string,
): string | null {
  const panid = new ClientAnimationData();
  if (!panid.Init(pakr, tam, src)) return null;
  const nStrip = panid.GetStripIndex('help');
  if (nStrip < 0) return null;
  const rc = panid.GetBounds(nStrip, 0);
  const cx = rc.right - rc.left;
  const cy = rc.bottom - rc.top;
  if (cx <= 0 || cy <= 0) return null;
  const dib = new CpuDibBitmap(cx, cy);
  // DrawFrame places the frame at (x - origin); GetBounds is relative to the origin, so
  // offset by (-left, -top) to land the frame's bbox at (0, 0) in the dib (PaintChunk does
  // the same x - rc.left, y - rc.top).
  panid.DrawFrame(nStrip, 0, dib, -rc.left, -rc.top, KSIDE_HELP, null);
  return titleArtToDataUrl({ cx, cy, rgba: dib.rgba });
}
